import { createPortal } from "react-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, CheckCheck, Inbox, Layers, Settings2, X } from "lucide-react";
import type {
  Notification as WorkspaceNotification,
  NotificationAlert,
  NotificationDigest,
  Page,
  WorkspaceSummary,
} from "@sol-office/contracts";
import { api, errorMessage, patch, post, RequestError } from "../api";
import { EmptyState, ErrorNotice, Modal, PageHeading, Spinner } from "../ui";
import { InboxPreferences } from "./Preferences";
import { connectNotifications } from "./live";
import {
  changedEvent,
  revokedEvent,
  NotificationCard,
  notificationTime,
  safeNotificationHref,
  signalNotificationChange,
} from "./shared";
import "./notifications.css";

type Navigate = (href: string) => void;
type InboxState = { unreadCount: number; cursor: string };
type DigestSummary = Pick<
  NotificationDigest,
  "id" | "createdAt" | "periodStart" | "periodEnd"
>;
const unavailable = (e: unknown) =>
  e instanceof RequestError && [401, 403, 404].includes(e.status);

export function NotificationWidget({
  workspace,
  navigate,
}: {
  workspace: WorkspaceSummary;
  navigate: Navigate;
}) {
  const [count, setCount] = useState(0),
    [live, setLive] = useState(false),
    [error, setError] = useState("");
  const [alert, setAlert] = useState<NotificationAlert | null>(null),
    [busy, setBusy] = useState(false);
  const generation = useRef(0),
    controller = useRef<AbortController | null>(null),
    alive = useRef(true);
  const clear = useCallback(() => {
    generation.current++;
    controller.current?.abort();
    setCount(0);
    setAlert(null);
  }, []);
  const load = useCallback(async () => {
    const requestId = ++generation.current;
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    try {
      const [state, alerts] = await Promise.all([
        api<InboxState>(`/workspaces/${workspace.id}/notifications/state`, {
          signal: request.signal,
        }),
        api<Page<NotificationAlert>>(
          `/workspaces/${workspace.id}/notification-alerts?limit=1`,
          { signal: request.signal },
        ),
      ]);
      if (!alive.current || requestId !== generation.current) return;
      setCount(state.unreadCount);
      setAlert(alerts.items[0] ?? null);
      setError("");
    } catch (e) {
      if (
        alive.current &&
        requestId === generation.current &&
        !request.signal.aborted
      ) {
        if (unavailable(e)) clear();
        setError(errorMessage(e));
      }
    }
  }, [workspace.id, clear]);
  useEffect(() => {
    alive.current = true;
    void load();
    let debounce: ReturnType<typeof setTimeout> | undefined;
    const changed = () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        signalNotificationChange();
      }, 75);
    };
    const stop = connectNotifications(workspace.id, changed, setLive, clear);
    const signal = () => {
      void load();
    };
    window.addEventListener(changedEvent, signal);
    const timer = setInterval(() => {
      void load();
    }, 10000);
    return () => {
      alive.current = false;
      clearTimeout(debounce);
      clearInterval(timer);
      window.removeEventListener(changedEvent, signal);
      stop();
      generation.current++;
      controller.current?.abort();
    };
  }, [workspace.id, load, clear]);
  const acknowledge = async () => {
    if (!alert) return;
    setBusy(true);
    try {
      await post(`/notification-alerts/${alert.id}/ack`, {});
      if (alive.current) {
        setAlert(null);
        await load();
      }
    } catch (e) {
      if (alive.current) {
        if (unavailable(e)) clear();
        setError(errorMessage(e));
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  return (
    <>
      <button
        className="icon-button inbox-bell"
        onClick={() => navigate(`/w/${workspace.id}/notifications`)}
        aria-label={`Notifications${count ? `, ${count} unread` : ""}`}
        title={
          error
            ? "Notifications could not refresh. Open your inbox to retry."
            : live
              ? "Notifications are live"
              : "Notifications refresh automatically"
        }
      >
        <Bell size={19} />
        {count > 0 && (
          <span className="inbox-badge">{count > 99 ? "99+" : count}</span>
        )}
      </button>
      {alert &&
        createPortal(
          <aside
            className="inbox-alert"
            role="status"
            aria-live="polite"
            aria-label="New workspace updates"
          >
            <div className="inbox-alert-heading">
              <span>
                <Bell size={17} /> A few things for you
              </span>
              <button
                className="icon-button"
                onClick={() => void acknowledge()}
                disabled={busy}
                aria-label="Dismiss notification alert"
              >
                <X size={17} />
              </button>
            </div>
            {alert.items.slice(0, 3).map((item) => (
              <button
                key={item.id}
                className="inbox-alert-item"
                disabled={busy}
                onClick={() =>
                  void (async () => {
                    setBusy(true);
                    try {
                      const { notification } = await patch<{
                        notification: WorkspaceNotification;
                      }>(`/notifications/${item.id}`, { read: true });
                      if (!alive.current) return;
                      const href = safeNotificationHref(
                        notification.href,
                        workspace.id,
                      );
                      signalNotificationChange();
                      if (href) navigate(href);
                    } catch (e) {
                      if (alive.current) {
                        if (unavailable(e)) clear();
                        setError(errorMessage(e));
                      }
                    } finally {
                      if (alive.current) setBusy(false);
                    }
                  })()
                }
              >
                <strong>{item.title}</strong>
                <span>{item.preview}</span>
              </button>
            ))}
            <div className="inbox-alert-footer">
              <button
                className="text-link"
                onClick={() => navigate(`/w/${workspace.id}/notifications`)}
              >
                Open inbox
              </button>
              <button
                className="button quiet"
                onClick={() => void acknowledge()}
                disabled={busy}
              >
                {busy ? "Dismissing…" : "Dismiss alert"}
              </button>
            </div>
            {error && <ErrorNotice message={error} />}
          </aside>,
          document.body,
        )}
    </>
  );
}

export function NotificationsPage({
  workspace,
  navigate,
}: {
  workspace: WorkspaceSummary;
  navigate: Navigate;
}) {
  const [items, setItems] = useState<WorkspaceNotification[]>([]),
    [cursor, setCursor] = useState<string | null>(null);
  const [state, setState] = useState<InboxState>({
    unreadCount: 0,
    cursor: "0",
  });
  const [filter, setFilter] = useState<
    "all" | "unread" | "dismissed" | "digests"
  >("all");
  const [source, setSource] = useState("all");
  const [digests, setDigests] = useState<DigestSummary[]>([]),
    [digestCursor, setDigestCursor] = useState<string | null>(null);
  const [selectedDigest, setSelectedDigest] = useState<string | null>(null),
    [preferences, setPreferences] = useState(false);
  const [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [busyId, setBusyId] = useState<string | null>(null);
  const generation = useRef(0),
    controller = useRef<AbortController | null>(null),
    pages = useRef(1),
    alive = useRef(true);
  const clear = () => {
    setItems([]);
    setDigests([]);
    setCursor(null);
    setDigestCursor(null);
    setState({ unreadCount: 0, cursor: "0" });
    setSelectedDigest(null);
  };
  const load = useCallback(
    async (next?: string) => {
      const requestId = ++generation.current;
      controller.current?.abort();
      const request = new AbortController();
      controller.current = request;
      setLoading(true);
      setError("");
      try {
        const statePromise = api<InboxState>(
          `/workspaces/${workspace.id}/notifications/state`,
          { signal: request.signal },
        );
        if (filter === "digests") {
          const query = new URLSearchParams({
            limit: "50",
            ...(next ? { cursor: next } : {}),
          });
          const [result, fresh] = await Promise.all([
            api<Page<DigestSummary>>(
              `/workspaces/${workspace.id}/notification-digests?${query}`,
              { signal: request.signal },
            ),
            statePromise,
          ]);
          if (!alive.current || requestId !== generation.current) return;
          setDigests((old) =>
            next
              ? [
                  ...old,
                  ...result.items.filter(
                    (x) => !old.some((y) => y.id === x.id),
                  ),
                ]
              : result.items,
          );
          setDigestCursor(result.nextCursor);
          setState(fresh);
        } else {
          const readPage = (after?: string) => {
            const query = new URLSearchParams({
              limit: "50",
              ...(filter === "unread" ? { unread: "true" } : {}),
              dismissed: filter === "dismissed" ? "true" : "false",
              ...(source !== "all" ? { kind: source } : {}),
              ...(after ? { cursor: after } : {}),
            });
            return api<Page<WorkspaceNotification>>(
              `/workspaces/${workspace.id}/notifications?${query}`,
              { signal: request.signal },
            );
          };
          const [first, fresh] = await Promise.all([
            readPage(next),
            statePromise,
          ]);
          let collected = first.items,
            after = first.nextCursor;
          if (!next) {
            const seen = new Set<string>();
            for (let i = 1; i < pages.current && after; i++) {
              if (seen.has(after))
                throw new Error(
                  "The inbox could not continue loading. Refresh and try again.",
                );
              seen.add(after);
              const page = await readPage(after);
              collected = [...collected, ...page.items];
              after = page.nextCursor;
            }
          }
          if (!alive.current || requestId !== generation.current) return;
          setItems((old) => {
            const all = next ? [...old, ...collected] : collected;
            return all.filter(
              (x, i) => all.findIndex((y) => y.id === x.id) === i,
            );
          });
          if (next) pages.current++;
          setCursor(after);
          setState(fresh);
        }
      } catch (e) {
        if (
          alive.current &&
          requestId === generation.current &&
          !request.signal.aborted
        ) {
          if (unavailable(e)) clear();
          setError(errorMessage(e));
        }
      } finally {
        if (alive.current && requestId === generation.current)
          setLoading(false);
      }
    },
    [workspace.id, filter, source],
  );
  useEffect(() => {
    alive.current = true;
    pages.current = 1;
    clear();
    void load();
    const changed = () => {
      void load();
    };
    const revoked = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== workspace.id) return;
      generation.current++;
      controller.current?.abort();
      clear();
      setPreferences(false);
      setLoading(false);
      setError("Your access to this workspace is no longer available.");
    };
    window.addEventListener(changedEvent, changed);
    window.addEventListener(revokedEvent, revoked);
    const timer = setInterval(changed, 10000);
    return () => {
      alive.current = false;
      generation.current++;
      controller.current?.abort();
      clearInterval(timer);
      window.removeEventListener(changedEvent, changed);
      window.removeEventListener(revokedEvent, revoked);
    };
  }, [load]);
  const update = async (
    item: WorkspaceNotification,
    body: { read?: boolean; dismissed?: boolean },
  ) => {
    setBusyId(item.id);
    setError("");
    try {
      await patch(`/notifications/${item.id}`, body);
      if (alive.current) {
        await load();
        signalNotificationChange();
      }
    } catch (e) {
      if (alive.current) {
        if (unavailable(e))
          setItems((old) => old.filter((x) => x.id !== item.id));
        setError(errorMessage(e));
      }
    } finally {
      if (alive.current) setBusyId(null);
    }
  };
  const open = async (item: WorkspaceNotification) => {
    const href = safeNotificationHref(item.href, workspace.id);
    if (!href) {
      setError("This notification does not have a valid workspace link.");
      return;
    }
    setBusyId(item.id);
    setError("");
    try {
      const { notification } = await patch<{
        notification: WorkspaceNotification;
      }>(`/notifications/${item.id}`, { read: true });
      const currentHref = safeNotificationHref(notification.href, workspace.id);
      if (alive.current && currentHref) {
        signalNotificationChange();
        navigate(currentHref);
      }
    } catch (e) {
      if (alive.current) {
        if (unavailable(e))
          setItems((old) => old.filter((x) => x.id !== item.id));
        setError(errorMessage(e));
      }
    } finally {
      if (alive.current) setBusyId(null);
    }
  };
  const readAll = async () => {
    const throughSequence = state.cursor;
    setBusyId("all");
    setError("");
    try {
      await post(`/workspaces/${workspace.id}/notifications/read`, {
        throughSequence,
      });
      if (alive.current) {
        await load();
        signalNotificationChange();
      }
    } catch (e) {
      if (alive.current) setError(errorMessage(e));
    } finally {
      if (alive.current) setBusyId(null);
    }
  };
  return (
    <div className="inbox-page">
      <PageHeading
        eyebrow="CLOSE TO WHAT MATTERS"
        title="A place to catch up."
        description="Conversations, plans and shared work, together in your inbox."
      >
        <button
          className="button secondary"
          onClick={() => setPreferences(true)}
        >
          <Settings2 size={16} /> Preferences
        </button>
        <button
          className="button primary"
          disabled={!state.unreadCount || !!busyId}
          onClick={() => void readAll()}
        >
          <CheckCheck size={17} /> Mark all read
        </button>
      </PageHeading>
      <div className="inbox-toolbar">
        <div className="tabs" role="tablist" aria-label="Inbox view">
          {(["all", "unread", "dismissed", "digests"] as const).map((value) => (
            <button
              key={value}
              role="tab"
              aria-selected={filter === value}
              className={filter === value ? "selected" : ""}
              onClick={() => setFilter(value)}
            >
              {value === "all"
                ? "All updates"
                : value === "unread"
                  ? `Unread${state.unreadCount ? ` (${state.unreadCount})` : ""}`
                  : value === "dismissed"
                    ? "Dismissed"
                    : "Digests"}
            </button>
          ))}
        </div>
        {filter !== "digests" && (
          <label className="inbox-kind-filter">
            <span>Update type</span>
            <select value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="all">All types</option>
              {[
                "chat.message",
                "chat.mention",
                "calendar.invited",
                "calendar.updated",
                "calendar.cancelled",
                "calendar.rsvp",
                "calendar.reminder",
                "task.assigned",
                "task.updated",
                "task.comment",
                "task.mention",
                "task.reminder",
                "document.comment",
                "document.mention",
              ].map((kind) => (
                <option value={kind} key={kind}>
                  {kind.replace(".", " · ")}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {error && <ErrorNotice message={error} retry={() => void load()} />}
      <section
        className="panel inbox-list"
        aria-label={
          filter === "digests"
            ? "Notification digests"
            : "Workspace notifications"
        }
        aria-busy={loading}
      >
        {loading && !(filter === "digests" ? digests.length : items.length) ? (
          <div className="panel-loading">
            <Spinner label="Gathering your updates…" />
          </div>
        ) : filter === "digests" ? (
          <>
            {digests.length ? (
              digests.map((digest) => (
                <button
                  className="inbox-digest-row"
                  key={digest.id}
                  onClick={() => setSelectedDigest(digest.id)}
                >
                  <span className="inbox-source-icon">
                    <Layers size={21} />
                  </span>
                  <span>
                    <strong>Your workspace digest</strong>
                    <small>
                      {notificationTime(digest.periodStart)} —{" "}
                      {notificationTime(digest.periodEnd)}
                    </small>
                  </span>
                  <span>Open digest</span>
                </button>
              ))
            ) : (
              <EmptyState icon={Layers} title="Your next catch-up, collected.">
                Choose a daily or weekly digest in Preferences. Saved digests
                will appear here.
              </EmptyState>
            )}
            {digestCursor && (
              <div className="load-more">
                <button
                  className="button secondary"
                  disabled={loading}
                  onClick={() => void load(digestCursor)}
                >
                  Load more digests
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {items.length ? (
              items.map((item) => (
                <NotificationCard
                  key={item.id}
                  item={item}
                  busy={busyId === item.id}
                  onOpen={() => void open(item)}
                  onRead={() => void update(item, { read: !item.readAt })}
                  onDismiss={() =>
                    void update(item, { dismissed: !item.dismissedAt })
                  }
                />
              ))
            ) : (
              <EmptyState
                icon={Inbox}
                title={
                  filter === "unread"
                    ? "You’re all caught up."
                    : "A little quiet for now."
                }
              >
                {filter === "dismissed"
                  ? "Updates you dismiss will be collected here."
                  : "Updates you can access will appear here as your team works."}
              </EmptyState>
            )}
            {cursor && (
              <div className="load-more">
                <button
                  className="button secondary"
                  disabled={loading}
                  onClick={() => void load(cursor)}
                >
                  Load more updates
                </button>
              </div>
            )}
          </>
        )}
      </section>
      <p className="inbox-help inbox-footer-note">
        Your inbox follows the permissions of the original work. Quiet hours
        pause alerts while updates continue to arrive.
      </p>
      {preferences && (
        <InboxPreferences onClose={() => setPreferences(false)} />
      )}
      {selectedDigest && (
        <DigestView
          key={selectedDigest}
          id={selectedDigest}
          workspaceId={workspace.id}
          navigate={navigate}
          onClose={() => setSelectedDigest(null)}
        />
      )}
    </div>
  );
}

function DigestView({
  id,
  workspaceId,
  navigate,
  onClose,
}: {
  id: string;
  workspaceId: string;
  navigate: Navigate;
  onClose: () => void;
}) {
  const [digest, setDigest] = useState<NotificationDigest | null>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const generation = useRef(0),
    controller = useRef<AbortController | null>(null);
  const load = useCallback(
    async (cursor?: string) => {
      const current = ++generation.current;
      controller.current?.abort();
      const request = new AbortController();
      controller.current = request;
      setLoading(true);
      setError("");
      try {
        const result = await api<{ digest: NotificationDigest }>(
          `/notification-digests/${id}?${new URLSearchParams({ limit: "50", ...(cursor ? { cursor } : {}) })}`,
          { signal: request.signal },
        );
        if (current === generation.current)
          setDigest((old) =>
            cursor && old
              ? {
                  ...result.digest,
                  items: [...old.items, ...result.digest.items],
                }
              : result.digest,
          );
      } catch (e) {
        if (!request.signal.aborted && current === generation.current) {
          if (unavailable(e)) setDigest(null);
          setError(errorMessage(e));
        }
      } finally {
        if (current === generation.current) setLoading(false);
      }
    },
    [id],
  );
  useEffect(() => {
    void load();
    const changed = () => {
      void load();
    };
    window.addEventListener(changedEvent, changed);
    return () => {
      generation.current++;
      controller.current?.abort();
      window.removeEventListener(changedEvent, changed);
    };
  }, [load]);
  return (
    <Modal
      title="Your workspace digest"
      subtitle={
        digest
          ? `${notificationTime(digest.periodStart)} — ${notificationTime(digest.periodEnd)}`
          : undefined
      }
      onClose={onClose}
    >
      {error && <ErrorNotice message={error} retry={() => void load()} />}
      {loading && !digest ? (
        <Spinner label="Opening your digest…" />
      ) : (
        digest && (
          <div className="inbox-digest-content">
            {digest.items.map((item) => (
              <NotificationCard
                key={item.id}
                item={item}
                onOpen={() => {
                  const href = safeNotificationHref(item.href, workspaceId);
                  if (href) navigate(href);
                }}
              />
            ))}
            {!digest.items.length && (
              <p>No accessible updates remain in this digest.</p>
            )}
            {digest.nextCursor && (
              <button
                className="button secondary"
                disabled={loading}
                onClick={() => void load(digest.nextCursor!)}
              >
                Load more digest updates
              </button>
            )}
            <p className="inbox-help">
              Opening a digest keeps the read state of its original updates.
            </p>
          </div>
        )
      )}
    </Modal>
  );
}
